<template>
  <ak-form class="flex mt-small" @submit="change">
    <ak-input
      v-model="searchFilter"
      type="search"
      icon="search"
      placeholder="Search Projects"
      style="flex-grow: 1"
      @right-icon-click="change"
    />
    <ak-dropdown
      position-x="right"
      position-y="bottom"
      aria-role="list"
      size="auto"
      class="mr-tiny ml-small"
    >
      <template #trigger>
        <ak-button
          circle
          color="transparent"
          class="ak-btn--single-icon--tiny"
          size="small"
        >
          <ak-icon type="funnel-b" size="big" />
        </ak-button>
      </template>
      <template #default>
        <ak-dropdown-item v-if="showSort" custom>
          <ak-field
            label="Sort By"
            custom-class="color-grey"
            margin-bottom="mb-none"
          >
            <ak-radio-group
              v-model="sortOrder"
              name="sort"
              class="text-small text-nowrap"
              :options="sortOptions"
              vertical
            />
          </ak-field>
        </ak-dropdown-item>
        <ak-dropdown-item v-if="showSort && showFilter" separator />
        <ak-dropdown-item v-if="showFilter" custom>
          <ak-field
            label="Filter"
            custom-class="color-grey"
            margin-bottom="mb-none"
          >
            <ak-checkbox-group
              v-model="selectedFilters"
              name="filter"
              class="pt-tiny text-small text-nowrap"
              :options="filterOptions"
              vertical
              @change="setFilter"
            />
          </ak-field>
        </ak-dropdown-item>
      </template>
    </ak-dropdown>
  </ak-form>
</template>

<script>
import { defineComponent, ref, watch, nextTick } from 'vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import RadioGroup from '@akerolabs/akero-vue/src/components/radioGroup/RadioGroup.vue'
import CheckboxGroup from '@akerolabs/akero-vue/src/components/checkboxGroup/CheckboxGroup.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'

export default defineComponent({
  name: 'SearchFilter',
  components: {
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
    [RadioGroup.name]: RadioGroup,
    [CheckboxGroup.name]: CheckboxGroup,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Button.name]: Button,
    [Icon.name]: Icon,
  },
  props: {
    showSort: {
      type: Boolean,
      default: false,
    },
    showFilter: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: '',
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    sort: {
      type: String,
      default: '',
    },
  },
  emits: ['update:search', 'update:filter', 'update:sort', 'change'],
  setup(props, { emit }) {
    const searchFilter = ref(props.search)
    const sortOrder = ref(props.sort)
    const sortOptions = ref([
      { label: 'Chronological', value: 'desc' },
      { label: 'Alphabetical', value: 'asc' },
    ])
    const filterOptions = ref([
      { label: 'Include Archived', value: 'archived' },
      { label: 'Include Agency Activity', value: 'agencyActivity' },
    ])
    const selectedFilters = ref([])
    const filter = ref(props.filter)
    if (props.filter.archived) {
      selectedFilters.value.push('archived')
    }
    if (props.filter.agencyActivity) {
      selectedFilters.value.push('agencyActivity')
    }

    watch(
      () => props.sort,
      (value) => {
        sortOrder.value = value
      },
    )

    watch(sortOrder, (value) => {
      emit('update:sort', value)
    })

    watch(searchFilter, (value) => {
      emit('update:search', value)
    })

    watch(
      () => props.search,
      (value) => {
        searchFilter.value = value
      },
    )

    watch(
      () => props.filter,
      ({ archived, agencyActivity }) => {
        filter.value.archived = archived
        filter.value.agencyActivity = agencyActivity
        const temp = []
        if (archived) {
          temp.push('archived')
        }
        if (agencyActivity) {
          temp.push('agencyActivity')
        }
        selectedFilters.value = temp
      },
    )

    /**
     * @description Update the filter options
     */
    const setFilter = () => {
      const temp = {
        archived: false,
        agencyActivity: false,
      }
      if (selectedFilters.value.includes('archived')) {
        temp.archived = true
      }
      if (selectedFilters.value.includes('agencyActivity')) {
        temp.agencyActivity = true
      }
      filter.value = temp
      emit('update:filter', filter.value)
      change()
    }

    /**
     * @description Emits the change event when something to do with filters or search has changed
     * @param {Event} event
     */
    const change = (event) => {
      if (event) {
        event.preventDefault()
      }
      nextTick(() => {
        emit('change')
      })
    }

    return {
      searchFilter,
      sortOrder,
      sortOptions,
      selectedFilters,
      filterOptions,
      setFilter,
      change,
    }
  },
})
</script>
